import * as React from "react";
import { DocumentTextIcon, FolderIcon } from "@heroicons/react/24/outline";

import { Users } from "lucide-react";
import LocationsModal from "../modals/locationsModal";

import { DriveRowSummary } from "./driveRowSummary";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toDefaultDateFormat } from "@/helpers/dates";
import { createBase64Image, defaultUserAvatar } from "@/helpers/images";
import { FolderItem, Item } from "@/types/fse";
import { useNavigate } from "react-router";
import { useGetUserQuery } from "@/api/users-admin";
import { DriveRowButtons } from "@/views/drive/components/driveRowButtons";
import { TxType } from "@/types/transaction";
import * as analytics from "@/services/analytics";

interface DriveRowProps {
  item: Item;
  progressForItem: Record<string, [number, TxType]>;
  parentFolder: FolderItem | null;
  hiddenButtons?: Array<string>;
}

export const DriveRow: React.FC<DriveRowProps> = ({ item, progressForItem, parentFolder, hiddenButtons = [] }) => {
  const [expanded, setExpanded] = React.useState(false);
  const navigate = useNavigate();

  const { data: owner } = useGetUserQuery(item.owner_id);

  const toggleExpanded = () => {
    if (!expanded) {
      analytics.showSummary(item.name);
    }

    setExpanded(prev => !prev);
  };

  const handleNavigate = () => {
    if (item.type === "folder") {
      navigate(`/drive/${item.id}`);
    }
  };

  const isShared = item.access === "public" || item.permissions.length > 1;

  return (
    <>
      <div className="relative flex flex-col items-start gap-4 p-4 hover:bg-ivory-mist-400 md:flex-row md:items-center md:p-6">
        <div className="relative w-full md:w-1/2 lg:w-6/12">
          <div className="flex cursor-pointer items-center" onClick={handleNavigate}>
            {item.type === "folder" ? (
              <FolderIcon className="mr-2 h-5 w-5 text-yellow-500" />
            ) : (
              <DocumentTextIcon className="mr-2 h-5 w-5 text-blue-500" />
            )}
            <span className="flex truncate font-medium">
              {item.name}
              {isShared && <Users className="ml-2 mt-1 h-3 w-3 text-gray-500" />}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center text-xs text-gray-500">
            <span className="mr-2" title={item.created_at}>
              {toDefaultDateFormat(item.created_at)}
            </span>
            <Avatar className="h-5 w-5">
              <AvatarImage
                src={owner?.avatar ? createBase64Image(owner.avatar) : defaultUserAvatar}
                alt={owner?.name || "User"}
              />
              <AvatarFallback className="text-[10px]">{owner ? owner.name[0] : "U"}</AvatarFallback>
            </Avatar>
            <span className="ml-2 truncate">{owner?.name || owner?.username}</span>
            <LocationsModal itemName={item.name} locations={item.locations} />
            {item.type === "file" && (
              <Button onClick={toggleExpanded} variant="ghost" size="sm" className="ml-2 p-1 text-xs">
                {expanded ? "Hide Summary" : "Show Summary"}
              </Button>
            )}
          </div>
        </div>
        <DriveRowButtons
          parentFolder={parentFolder}
          item={item}
          progressForItem={progressForItem}
          hiddenButtons={hiddenButtons}
        />
      </div>
      {item.type === "file" && expanded && <DriveRowSummary item={item} />}
    </>
  );
};
